/**
 * Преобразует ФИО в формат с инициалами.
 * "Иванов" -> "Иванов"
 * "Сидоров Олег" -> "Сидоров О."
 * "Петров Петр Петрович" -> "Петров П.П."
 */
export const getShortName = (fullName: string): string => {
  if (!fullName) return ''

  const parts = fullName.trim().split(/\s+/)

  // Если только Фамилия — возвращаем как есть
  if (parts.length === 1) return parts[0]

  // Если Фамилия Имя — "Сидоров О."
  if (parts.length === 2) {
    return `${parts[0]} ${parts[1][0]}.`
  }

  // Если Фамилия Имя Отчество (3 и более слов) — "Петров П.П."
  return `${parts[0]} ${parts[1][0]}.${parts[2][0]}.`
}

/**
 * Декодирует строку из API (с тегами {{type:id}}) в текст для отображения (с именами).
 * @param content - Исходный текст с тегами
 * @param participants - Список объектов участников для поиска соответствий по ID
 */
export function decodeParticipants(
  content: string,
  participants: Array<{ id: string; participant_type: string; full_name: string }>,
): string {
  // Находим все вхождения формата {{тип:uuid}} и заменяем их на короткие имена
  return content.replace(
    /\{\{(employee|patient|visitor):([a-f0-9-]+)\}\}/g,
    (_, type, id) => {
      // Ищем участника в массиве по ID из тега
      const p = participants.find(p => p.id === id)
      // Если не нашли (удален), возвращаем пустую строку, иначе — короткое имя
      if (!p) return ''
      return getShortName(p.full_name)
    },
  )
}

/**
 * Кодирует текст из textarea обратно в формат с тегами {{type:id}} для сохранения.
 * @param content - Текст из текстового поля с именами
 * @param participants - Список участников для сопоставления имен с их ID
 */
export function encodeParticipants(
  content: string,
  participants: Array<{ id: string; participant_type: string; full_name: string }>,
): string {
  let encoded = content

  // 1. Сортируем участников по длине короткого имени (от длинных к коротким).
  // Это критично, чтобы "Иванов И.И." не превратился в "Иванов {{...}}" + случайная "И."
  const sortedParticipants = [...participants].sort((a, b) =>
    getShortName(b.full_name).length - getShortName(a.full_name).length,
  )

  // 2. Проходим по каждому участнику и ищем его имя в тексте
  sortedParticipants.forEach(p => {
    const shortName = getShortName(p.full_name)
    if (!shortName) return

    // 3. Экранируем точки для корректной работы регулярного выражения
    const safeName = shortName.replace(/\./g, '\\.')
    const regex = new RegExp(safeName, 'g')

    // 4. Заменяем текстовое имя на технический тег
    encoded = encoded.replace(regex, `{{${p.participant_type}:${p.id}}}`)
  })

  return encoded
}
